// The vocabulary of the desk. These lists mirror the Postgres enums in
// supabase/migrations/202610072301_foundation.sql one for one; `pnpm test:unit`
// has a test that fails if the two drift apart.

export const ROLES = ['owner', 'sales', 'care', 'ads', 'caller'] as const;
export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  owner: 'Owner',
  sales: 'Sales',
  care: 'First contact',
  ads: 'Ads & systems',
  caller: 'External caller',
};

export const PERMS = [
  'today',
  'calls',
  'pipeline',
  'tasks',
  'calendar',
  'whatsapp',
  'coldemail',
  'coldcall',
  'automations',
  'integrations',
  'team',
  'playbook',
] as const;
export type Perm = (typeof PERMS)[number];

/** Defaults a role starts with. Per-person toggles override them. */
export const ROLE_PERMS: Record<Role, readonly Perm[]> = {
  owner: PERMS,
  sales: ['today', 'calls', 'pipeline', 'tasks', 'calendar'],
  care: ['today', 'pipeline', 'tasks', 'calendar'],
  ads: ['today', 'pipeline', 'integrations'],
  caller: ['coldcall'],
};

export const MARKETS = ['TR', 'UK'] as const;
export type Market = (typeof MARKETS)[number];

export const MARKET_TZ: Record<Market, string> = {
  TR: 'Europe/Istanbul',
  UK: 'Europe/London',
};

export const STAGES = [
  { id: 'new', label: 'New lead', code: 'NEW' },
  { id: 'contacted', label: 'Contacted', code: 'CON' },
  { id: 'fu1', label: 'Follow-up 1', code: 'FU1' },
  { id: 'fu2', label: 'Follow-up 2', code: 'FU2' },
  { id: 'fu3', label: 'Follow-up 3', code: 'FU3' },
  { id: 'qualified', label: 'Qualified', code: 'QUA' },
  { id: 'call1', label: 'Discovery call', code: 'DC1' },
  { id: 'noshow', label: 'No-show', code: 'NSH' },
  { id: 'offer', label: 'Building offer', code: 'OFR' },
  { id: 'call2', label: 'Offer call', code: 'OC2' },
  { id: 'decision', label: 'Deciding', code: 'DEC' },
  { id: 'won', label: 'Client', code: 'WON' },
  { id: 'nurture', label: 'Nurture', code: 'NUR' },
  { id: 'lost', label: 'Lost', code: 'LOST' },
  { id: 'disq', label: 'Disqualified', code: 'DQ' },
] as const;

export type Stage = (typeof STAGES)[number]['id'];

/** Stages that take a lead out of the active pipeline. */
export const CLOSED_STAGES = ['won', 'nurture', 'lost', 'disq'] as const satisfies readonly Stage[];

export const isClosed = (stage: Stage): boolean =>
  (CLOSED_STAGES as readonly string[]).includes(stage);

export const stageLabel = (stage: Stage): string =>
  STAGES.find((s) => s.id === stage)?.label ?? stage;

/** The compressed route shown above a lead: NEW › DC1 › OFR › OC2 › WON. */
export const ROUTE = [
  { code: 'NEW', stages: ['new', 'contacted', 'fu1', 'fu2', 'fu3', 'qualified'] },
  { code: 'DC1', stages: ['call1', 'noshow'] },
  { code: 'OFR', stages: ['offer'] },
  { code: 'OC2', stages: ['call2', 'decision'] },
  { code: 'WON', stages: ['won'] },
] as const;

export const VIEWS = [
  { id: 'today', label: 'Today', group: 'Sales', perm: 'today' },
  { id: 'calls', label: 'Call reviews', group: 'Sales', perm: 'calls' },
  { id: 'pipeline', label: 'Pipeline', group: 'Sales', perm: 'pipeline' },
  { id: 'tasks', label: 'Tasks', group: 'Sales', perm: 'tasks' },
  { id: 'calendar', label: 'Calendar', group: 'Sales', perm: 'calendar' },
  { id: 'whatsapp', label: 'WhatsApp', group: 'Sales', perm: 'whatsapp' },
  { id: 'coldemail', label: 'Cold email', group: 'Outreach', perm: 'coldemail' },
  { id: 'coldcall', label: 'Cold calling', group: 'Outreach', perm: 'coldcall' },
  { id: 'automations', label: 'Automations', group: 'Admin', perm: 'automations' },
  { id: 'integrations', label: 'Integrations', group: 'Admin', perm: 'integrations' },
  { id: 'team', label: 'Team & access', group: 'Admin', perm: 'team' },
  { id: 'playbook', label: 'Playbook', group: 'Admin', perm: 'playbook' },
] as const satisfies readonly { id: string; label: string; group: string; perm: Perm }[];

export type ViewId = (typeof VIEWS)[number]['id'];

/** The four tabs each role gets on mobile, in order. */
export const MOBILE_TABS: Record<Role, readonly ViewId[]> = {
  owner: ['today', 'pipeline', 'tasks', 'calendar'],
  sales: ['today', 'calls', 'pipeline', 'tasks'],
  care: ['today', 'pipeline', 'tasks', 'calendar'],
  ads: ['today', 'pipeline', 'integrations'],
  caller: ['coldcall'],
};

export const AUTOMATION_KEYS = [
  'speed_to_lead',
  'no_reply_ladder',
  'before_the_call',
  'no_show',
  'post_offer',
  'nurture',
  'client_d30_d60',
] as const;
export type AutomationKey = (typeof AUTOMATION_KEYS)[number];

export const ACTION_KINDS = [
  'wa_template',
  'wa_free',
  'email',
  'task',
  'caller_queue',
  'capi_event',
  'stage_change',
  'notify',
  'next_step',
  'ghl_tag',
  'create_client',
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export const REPLY_CATEGORIES = [
  'interested',
  'not_now',
  'other_contact',
  'not_interested',
  'out_of_office',
  'unsubscribe',
  'auto_reply',
  'unknown',
] as const;
export type ReplyCategory = (typeof REPLY_CATEGORIES)[number];

export const CALL_OUTCOMES = [
  'no_answer',
  'receptionist',
  'call_back',
  'send_email',
  'not_interested',
  'booked',
  'wrong_number',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

/** Quiet hours, local to the lead. Outside them an automation queues. */
export const QUIET_HOURS = { from: 9, to: 19 } as const;
